import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { Injectable, Inject } from '@angular/core';
import { first } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { getValidAuthToken } from '../ngrx/state/auth.state';
import { APP_CONFIG } from '../tokens/app-config.token';
import { Environment } from '../../../environments/environment.interface';
import { ApplicationState } from '../../reducers';
import { LogoutAction } from '../ngrx/action/auth.action';
import { DEFAULT_MAINTENANCE_MESSAGE, MaintenanceErrorCode, MaintenanceHttpErrorCode } from './dn-interceptor.config';
import { Message, MessageType, NotyService } from '@dn/libs/ui/dn-common';

@Injectable({
  providedIn: 'root',
})
export class DnInterceptorService implements HttpInterceptor {
  whiteListEndpoints: string[];

  constructor(
    private readonly noti: NotyService,
    private readonly authStore: Store<ApplicationState>,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.whiteListEndpoints = [`${this.appConfig.config_backend_url}/version`];
  }

  /**
   * Intercept the request sent using HttpClient
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const promise = this.handleRequest(request, next);
    return this.convertPromiseToObservable(promise);
  }

  private async handleRequest(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    // for all request the go to identity
    if (!this.isBackendRequest(request) || this.whiteListEndpoints.includes(request.url) || request.url.includes(`${this.appConfig.identity_backend_url}`)) {
      return next
        .handle(request)
        .toPromise()
        .catch((err) => this.handleError(err));
    }

    // all requests related to checkout, or any other non-identity requests

    const authToken = await this.getAuthTokenFromStore();
    if (!authToken) {
      this.authStore.dispatch(LogoutAction());
      return of(null).toPromise();
    }

    const builtReq = await this.buildBackendRequestPayload(request);
    const res = await next
      .handle(builtReq)
      .toPromise()
      .catch((err) => this.handleError(err));
    return res;
  }

  private buildHeadersWithAuthToken(token: string): HttpHeaders {
    const authorizationValue = this.buildHeaderAuthorizationValue(token);
    const headers = this.buildHeaderWithAutorizationValue(authorizationValue);
    return headers;
  }

  private buildHeaderWithAutorizationValue(authorizationValue: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: authorizationValue,
    });
  }

  private buildHeaderAuthorizationValue(token: string): string {
    let authorizationValue = '';
    if (token) {
      authorizationValue = `Bearer ${token}`;
    }
    return authorizationValue;
  }

  private handleError(err: HttpErrorResponse): Promise<any> | void {
    switch (err.status) {
      case MaintenanceHttpErrorCode:
        if (err.error.errorCode === MaintenanceErrorCode) {
          this.maintenancesEvent(err);
        } else {
          return throwError(err).toPromise();
        }
        break;
      default:
        return throwError(err).toPromise();
    }
  }

  private async buildBackendRequestPayload(request: HttpRequest<any>): Promise<HttpRequest<any>> {
    const authToken = await this.getAuthTokenFromStore();

    let headers = this.buildHeadersWithAuthToken(authToken);

    if (this.appConfig.name === 'dev') {
      headers = headers.append('team-id', this.appConfig.teamId);
      headers = headers.append('user-id', this.appConfig.userId);
      headers = headers.append('email', this.appConfig.email);
    }

    const transformedRequest = request.clone({
      headers,
      body: request.body,
    });

    return transformedRequest;
  }

  private async getAuthTokenFromStore(): Promise<string> {
    return this.authStore.select(getValidAuthToken).pipe(first()).toPromise();
  }

  private convertPromiseToObservable(promise: Promise<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return from(promise);
  }

  /**
   * Is the request to micro services backend
   */
  private isBackendRequest(request: HttpRequest<any>): boolean {
    return request.url.startsWith(this.appConfig.backend_domain);
  }
  private maintenancesEvent(errorResponse: HttpErrorResponse): void {
    const { error } = errorResponse || {};
    const { message } = error || {};
    const errorText = message || DEFAULT_MAINTENANCE_MESSAGE;
    const notiMessage: Message = new Message(MessageType.ERROR, errorText, null, 0);
    this.noti.postMessage(notiMessage);
  }
}
